import { useStaleContext } from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/_components/stale-context";
import { useCallback, useMemo, useState } from "react";
import {
  documentDbDelete,
  documentDbGet,
  documentDbGetAll,
  documentDbPut,
} from "@/shared/lib/storage/indexdb/document-db";
import { GSMDocumentKind } from "@/services/backend/documents/types/kind";
import {
  OfflineState,
  useOfflineContext,
} from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/_components/offline-context";

/**
 * Mutation defines a consistent interface to encapsulate
 * rtk query mutations and indexedDB mutations.
 *
 * The trigger mirrors an RTK Query mutation trigger: calling it returns a
 * handle exposing `unwrap()`, a promise that resolves with the result on
 * success and rejects on failure. This lets a `Mutation` be passed directly to
 * promise-based helpers such as `useDebouncedMutation`, regardless of whether
 * it is backed by an RTK Query mutation or an IndexedDB write.
 */
export type Mutation<Req, Res> = [
  (entity: Req) => { unwrap: () => Promise<Res> },
  { isLoading: boolean; error: unknown; isSuccess: boolean; reset: () => void },
];

export type Query<Res> = {
  data: Res | undefined;
  isLoading: boolean;
  error: unknown;
};

// TODO-JO
//  * the current state handling in the hooks allows inconsistencies
//  * It is possible for isLoading to be false, but the data is not yet
//    available.
//  * This must be refactored to be all in one state object.

export function useDocumentDBPut<Req, Res>(
  documentKind: GSMDocumentKind,
  documentId: string,
  storeName: string,
): Mutation<Req, Res> {
  const { addStale } = useStaleContext();
  const [state, setState] = useState<{
    isLoading: boolean;
    error: unknown;
    isSuccess: boolean;
  }>({
    isLoading: false,
    error: undefined,
    isSuccess: false,
  });

  const reset = useCallback(() => {
    setState({ isLoading: false, error: undefined, isSuccess: false });
  }, []);

  const put = useCallback(
    (request: Req) => {
      setState({ isLoading: true, error: undefined, isSuccess: false });
      // The result promise never rejects (errors are folded into `{ error }`),
      // so fire-and-forget callers that ignore the return value do not produce
      // an unhandled rejection. Only `unwrap()` surfaces the error by throwing.
      const resultPromise = documentDbPut(
        documentKind,
        documentId,
        storeName,
        request,
      )
        .then((stored): { data: Res } | { error: unknown } => {
          addStale(storeName);
          setState({ isLoading: false, error: undefined, isSuccess: true });
          return { data: stored as unknown as Res };
        })
        .catch((error): { data: Res } | { error: unknown } => {
          setState({
            isLoading: false,
            error: { status: 500 },
            isSuccess: false,
          });
          return { error };
        });

      return {
        unwrap: () =>
          resultPromise.then((result) => {
            if ("error" in result) {
              throw result.error;
            }
            return result.data;
          }),
      };
    },
    [addStale, documentId, documentKind, storeName],
  );

  const stateValue = useMemo(
    () => ({
      isLoading: state.isLoading,
      error: state.error,
      isSuccess: state.isSuccess,
      reset,
    }),
    [state.isLoading, state.error, state.isSuccess, reset],
  );

  return [put, stateValue];
}

export function useDocumentDBGet<Res>(
  documentKind: GSMDocumentKind,
  documentId: string,
  storeName: string,
  key: string,
): Query<Res> {
  const { offlineState } = useOfflineContext();
  const { stale, removeStale } = useStaleContext();
  const [dataKey, setDataKey] = useState<string | undefined>();
  const [data, setData] = useState<Res | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>();

  // Assumption: IndexedDB is only used when offline. Running
  // the query online without data or potentially
  // uninitialized database, errors could happen.
  // Updates are triggered by the stale context
  // similar "tags" in RTKQuery.
  if (
    offlineState === OfflineState.OFFLINE &&
    (stale.includes(storeName) || key !== dataKey) &&
    !isLoading
  ) {
    setIsLoading(true);
    documentDbGet<Res>(documentKind, documentId, storeName, key)
      .then((result) => {
        if (result === null) {
          setError({ status: 404 });
        } else {
          setData(result);
          setDataKey(key);
        }
        removeStale(storeName);
      })
      .catch(() => {
        setError({ status: 500 });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  return { data, isLoading, error };
}

export function useDocumentDBGetAll<Res>(
  documentKind: GSMDocumentKind,
  documentId: string,
  storeName: string,
): Query<Res[]> {
  const { offlineState } = useOfflineContext();
  const { stale, removeStale } = useStaleContext();
  const [data, setData] = useState<Res[] | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>();

  // Assumption: IndexedDB is only used when offline. Running
  // the query online without data or potentially
  // uninitialized database, errors could happen.
  // Updates are triggered by the stale context
  // similar "tags" in RTKQuery.
  if (
    offlineState === OfflineState.OFFLINE &&
    (stale.includes(storeName) || data === undefined) &&
    !isLoading
  ) {
    setIsLoading(true);
    documentDbGetAll<Res>(documentKind, documentId, storeName)
      .then((result) => {
        setData(result);
        removeStale(storeName);
      })
      .catch(() => {
        setError({ status: 500 });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  return { data, isLoading, error };
}

export function useDocumentDBDelete(
  documentKind: GSMDocumentKind,
  documentId: string,
  storeName: string,
): Mutation<string, null> {
  const { addStale } = useStaleContext();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>();
  const [isSuccess, setIsSuccess] = useState(false);
  const reset = () => {
    setError(undefined);
    setIsSuccess(false);
  };

  const del = (key: string) => {
    setIsLoading(true);
    setError(undefined);
    setIsSuccess(false);

    // The result promise never rejects (errors are folded into `{ error }`), so
    // fire-and-forget callers that ignore the return value do not produce an
    // unhandled rejection. Only `unwrap()` surfaces the error by throwing.
    const resultPromise = documentDbDelete(
      documentKind,
      documentId,
      storeName,
      key,
    )
      .then((): { data: null } | { error: unknown } => {
        addStale(storeName);
        setIsSuccess(true);
        return { data: null };
      })
      .catch((err): { data: null } | { error: unknown } => {
        setError({ status: 500 });
        return { error: err };
      })
      .finally(() => {
        setIsLoading(false);
      });

    return {
      unwrap: () =>
        resultPromise.then((result) => {
          if ("error" in result) {
            throw result.error;
          }
          return result.data;
        }),
    };
  };

  return [del, { isLoading, error, isSuccess, reset }];
}
